/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { bbox } from '@turf/turf';
import type { MultiPolygon, Polygon } from 'geojson';
import type { View } from 'ol';
import { boundingExtent } from 'ol/extent';
import { fromLonLat } from 'ol/proj';
import React, { useEffect } from 'react';
import { ErrorBoundary, PROJECTION } from '@opengeoweb/shared';
import type { OGCWMSWMTSGetCapabilities } from '@opengeoweb/webmap';
import {
  DefaultBaseLayers,
  MapControls,
  OpenLayersMapView,
  OpenLayersZoomControl,
  WMSLayer,
} from '@opengeoweb/webmap-react';

interface InfoDialogWMSMapProps {
  view: View;
  geometry: Polygon | MultiPolygon;
  dataLink: string;
  getCapabilitiesJSON: OGCWMSWMTSGetCapabilities;
}

export const InfoDialogWMSMap: React.FC<InfoDialogWMSMapProps> = ({
  view,
  geometry,
  dataLink,
  getCapabilitiesJSON,
}: InfoDialogWMSMapProps) => {
  const mapContainerRef = React.useRef<HTMLDivElement>(null);
  const [mapSize, setMapSize] = React.useState<[number, number]>([0, 0]);
  const [extent, setExtent] = React.useState<number[] | undefined>(undefined);

  const props = {
    isFocused: false,
    view,
  };

  React.useEffect(() => {
    if (mapContainerRef.current) {
      const rect = mapContainerRef.current.getBoundingClientRect();
      setMapSize([rect.width, rect.height]);
    }
  }, []);

  const findLayerName = (link: string): string => {
    if (link.split(/(?<=layers=)/i)[1]) {
      return link.split(/(?<=layers=)/i)[1];
    }
    if (link.split(/(?<=layer=)/i)[1]) {
      return link.split(/(?<=layer=)/i)[1];
    }
    return '';
  };

  useEffect(() => {
    const [west, south, east, north] = bbox(geometry);
    const southwest = fromLonLat([west, south], PROJECTION.EPSG_3857.value);
    const northeast = fromLonLat([east, north], PROJECTION.EPSG_3857.value);
    setExtent(boundingExtent([southwest, northeast]));
  }, [geometry]);

  useEffect(() => {
    if (mapSize && extent) {
      view.fit(extent, { size: mapSize });
    }
  }, [extent, geometry.coordinates, mapSize, view]);

  return getCapabilitiesJSON?.WMS_Capabilities ? (
    <div
      data-testid="info-dialog-wms-map"
      ref={mapContainerRef}
      style={{
        width: '100%',
        paddingLeft: 12,
        paddingRight: 12,
        paddingBottom: 12,
      }}
    >
      <div style={{ aspectRatio: '2 / 1', width: '100%' }}>
        <OpenLayersMapView {...props}>
          <MapControls style={{ top: 0 }}>
            <OpenLayersZoomControl homeExtent={extent} />
          </MapControls>
          <DefaultBaseLayers />
          <ErrorBoundary>
            <WMSLayer
              layerName={findLayerName(dataLink)}
              layerOptions={{
                zIndex: 1,
                opacity: 0.5,
              }}
              styleName="default"
              url={dataLink}
            />
          </ErrorBoundary>
        </OpenLayersMapView>
      </div>
    </div>
  ) : null;
};
